import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "@tanstack/react-router";

export type Role = "admin" | "parent" | "student" | "instructor";

export interface UserPermissions {
  can_access_resources: boolean;
  [key: string]: boolean | undefined;
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  roles: Role[];
  permissions: UserPermissions | null;
  isAdmin: boolean;
  isParent: boolean;
  isStudent: boolean;
  isInstructor: boolean;
  primaryRole: Role | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refreshRoles: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function pickPrimary(roles: Role[]): Role | null {
  const order: Role[] = ["admin", "instructor", "student", "parent"];
  for (const r of order) if (roles.includes(r)) return r;
  return null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<UserPermissions | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchRoles = async (uid: string | undefined) => {
    if (!uid) {
      setRoles([]);
      setPermissions(null);
      return;
    }

    try {
      const [rolesResponse, permissionsResponse] = await Promise.all([
        supabase.from("user_roles").select("role").eq("user_id", uid),
        supabase.from("user_permissions").select("*").eq("user_id", uid).maybeSingle(),
      ]);

      if (rolesResponse.error) {
        console.error("Error fetching user_roles:", rolesResponse.error);
      } else {
        setRoles((rolesResponse.data ?? []).map((r) => r.role as Role));
      }

      if (permissionsResponse.error) {
        console.error("Error fetching user_permissions:", permissionsResponse.error);
      } else {
        setPermissions(permissionsResponse.data as UserPermissions | null);
      }
    } catch (err) {
      console.error("Unexpected error fetching auth roles/permissions:", err);
    }
  };

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let isInitialLoad = true;
    let lastFetchedUid: string | undefined = undefined;

    try {
      const { data } = supabase.auth.onAuthStateChange((event, ns) => {
        if (!isInitialLoad) {
          setLoading(true);
        }
        isInitialLoad = false;

        setSession(ns);
        setUser(ns?.user ?? null);

        const currentUid = ns?.user?.id;
        if (currentUid !== lastFetchedUid || !currentUid) {
          lastFetchedUid = currentUid;
          fetchRoles(currentUid).finally(() => setLoading(false));
        } else {
          setLoading(false);
        }
      });
      subscription = data.subscription;
    } catch (err) {
      console.error("Failed to initialize Supabase auth:", err);
      setLoading(false);
    }

    return () => {
      if (subscription) subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setRoles([]);
    setPermissions(null);
    navigate({ to: "/" });
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        roles,
        permissions,
        isAdmin: roles.includes("admin"),
        isParent: roles.includes("parent"),
        isStudent: roles.includes("student"),
        isInstructor: roles.includes("instructor"),
        primaryRole: pickPrimary(roles),
        loading,
        signOut,
        refreshRoles: () => fetchRoles(user?.id),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
